import React, { useCallback, useEffect } from 'react';
import { classes, IconButton, useSlug } from '@bloomberg/media-ui-core';
import { ExitThin } from '@bloomberg/media-ui-icons';
import { BannerCloseReason } from 'src/types/index';

import { TranslatedMessage } from '../utils/translate-messages';

import styles from './BreakingNews.module.css';

export type BreakingNewsProps = {
  headline: string;
  url?: string;
  identifier: string;
  onClose?: (reason: BannerCloseReason) => void;
  desktopBreakpointClassName?: string;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
};

export const BreakingNews = React.forwardRef<HTMLDivElement, BreakingNewsProps>(
  function BreakingNewsComponent(props: BreakingNewsProps, ref) {
    const {
      headline,
      url = '',
      identifier,
      onClose,
      desktopBreakpointClassName,
      trackingFunction = () => {},
    } = props;

    const [closed, setClosed] = React.useState(false);

    const cx = classes([styles.breakingNews, closed && styles.closed]);
    const componentName = useSlug('breaking-news');

    useEffect(() => {
      trackingFunction('impression', `breakingNews_${headline}`);
    }, []);

    const onClick = useCallback(() => {
      trackingFunction('click', `breakingNews_${headline}`);
    }, [headline]);

    const handleExit = useCallback((reason: BannerCloseReason) => {
      return function handler(_ev: React.MouseEvent<HTMLButtonElement>) {
        if (onClose) {
          onClose(reason);

          trackingFunction('click', 'breakingNews_close');
        }
        setClosed(true);
      };
    }, []);

    if (!headline) return null;

    return (
      <div className={cx} data-component={componentName} ref={ref}>
        <div
          className={classes([
            styles.contentContainer,
            desktopBreakpointClassName,
          ])}
        >
          <a className={styles.linkContainer} href={url} onClick={onClick}>
            <div className={styles.identifier}>{identifier}</div>
            <div className={styles.headline}>{headline}</div>
            <div className={styles.readMore}>
              <TranslatedMessage
                id="media-ui-nav_readMore"
                defaultMessage="Read more"
              />
            </div>
          </a>
          <IconButton
            onClick={handleExit('ExitClick')}
            className={styles.iconButton}
          >
            <ExitThin />
          </IconButton>
        </div>
      </div>
    );
  }
);
